import React, {FC, ReactNode, useMemo, useRef, useState} from 'react';

import {
  useConversationContextNamespaces,
  useSelectedConversationsDetails,
} from '../../../../../../../libs/inboxes/business/src/cells/conversations';
import {findDefaultTeamStatusForTicketStatusCategory} from '../../../../../../../libs/inboxes/business/src/ticketing/teamStatus';
import {KeyboardActionsEnum} from '../../../../../../../libs/shared/core/src/helpers/browser/keyboardHelpers';
import {useEventHandler} from '../../../../../../../libs/shared/core/src/helpers/react/hooks/useEventHandler';
import {
  FrontEvent,
  isKeyboardEvent,
} from '../../../../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {ConversationTeamStatusesEnum} from '../../../../../../../libs/shared/core/src/models/api/conversationCommonTypes';
import {TicketStatusModel} from '../../../../../../../libs/shared/core/src/models/api/ticketStatusModel';
import {CellsContext} from '../../../../../../../libs/shared/core/src/store/panels/cells/context/cellsContext';
import {ChromelessButton} from '../../../../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {DropdownPlacement} from '../../../../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {KeyboardEventHandlers} from '../../../../../../../libs/shared/ui/src/components/keyboard/keyboardContext';
import {KeyboardShortcuts} from '../../../../../../../libs/shared/ui/src/components/keyboard/keyboardShortcuts';
import {TooltipCoordinator} from '../../../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {PermissionCheck} from '../../../../../../../srcCommon/permissions/permissionCheck';
import {PermissionDenied} from '../../../../../../../srcCommon/permissions/permissionDenied';
import {useConversationStatusActionOfflinePermission} from '../../../../../../../srcCommon/permissions/useOfflinePermission';
import {useAppDispatch} from '../../../../../../helpers/appReduxHelpers';
import {makeFindConversationActionContext} from '../../../../../../helpers/models/conversationHelpers';
import {ConversationBaseModel} from '../../../../../../models/api/conversationModel';
import {PermissionsEnum} from '../../../../../../models/api/roleModel';
import {setConversationsTicketStatusAsyncAction} from '../../../../../../store/data/conversations/conversationsTicketingActions';
import {TicketStatusDropdownContainer} from '../../../../ticketing/ticketStatusDropdownContainer';
import {SafetyNetActionsEnum} from '../../safetyNets/safetyNetsContext';
import {useSafetyNetsCallback} from '../../safetyNets/safetyNetsCoordinator';

/*
 * Props.
 */

interface ConversationTicketStatusCoordinatorProps {
  cellsContext: CellsContext;
  conversations: ReadonlyArray<ConversationBaseModel>;
  className?: string;
  keyboardAction?: KeyboardActionsEnum;
  placement?: DropdownPlacement;
  renderButtonTooltip?: (isOpen: boolean) => ReactNode;
  renderButton: (buttonProps: ConversationTicketStatusButtonProps) => ReactNode;
  isTicketStatusDropdownOpen: boolean;
  onTicketStatusDropdownToggle: (isOpen: boolean) => void;
}

export interface ConversationTicketStatusButtonProps {
  isOpen: boolean;
  isDisabled: boolean;
}

/*
 * Component.
 */

export const ConversationTicketStatusCoordinator: FC<ConversationTicketStatusCoordinatorProps> = (props) => {
  const buttonRef = useRef<HTMLDivElement>(null);
  const dispatch = useAppDispatch();

  const {indicatedTicketStatus, areAllTicketingActionsEnabled} = useSelectedConversationsDetails(props);

  const contextNamespaces = useConversationContextNamespaces(props);
  const offlinePermission = useConversationStatusActionOfflinePermission(
    props.cellsContext,
    props.conversations,
  );

  const [openedByKeyboardAction, setOpenedByKeyboardAction] = useState(false);

  const {
    cellsContext,
    conversations,
    keyboardAction,
    isTicketStatusDropdownOpen,
    onTicketStatusDropdownToggle,
  } = props;

  /*
   * Event Handlers.
   */

  const onValidTicketStatusSelect = (ticketStatus: TicketStatusModel) => {
    dispatch(
      setConversationsTicketStatusAsyncAction(
        cellsContext,
        conversations.map(({id}) => id),
        ticketStatus.tagId,
        makeFindConversationActionContext(cellsContext, conversations),
      ),
    );
  };

  const onArchiveTicketStatusSelect = useSafetyNetsCallback({
    safetyNetActionType: SafetyNetActionsEnum.ARCHIVE,
    conversations,
    onActionValidated: onValidTicketStatusSelect,
    cellsContext,
  });

  const onTicketStatusSelect = (_event: unknown, ticketStatus: TicketStatusModel) => {
    if (ticketStatus.tagId === indicatedTicketStatus?.tagId) {
      return;
    }

    const teamStatus = findDefaultTeamStatusForTicketStatusCategory(ticketStatus.category);
    if (teamStatus === ConversationTeamStatusesEnum.OPEN) {
      onValidTicketStatusSelect(ticketStatus);
    } else {
      onArchiveTicketStatusSelect(ticketStatus);
    }
  };

  const onButtonClick = useEventHandler((event: FrontEvent) => {
    onTicketStatusDropdownToggle(!isTicketStatusDropdownOpen);
    setOpenedByKeyboardAction(isKeyboardEvent(event));
  });

  const onRequestClose = () => {
    onTicketStatusDropdownToggle(false);
    setOpenedByKeyboardAction(false);
  };

  const keyboardHandlers = useMemo<KeyboardEventHandlers>(
    () =>
      keyboardAction
        ? {
            [keyboardAction]: onButtonClick,
          }
        : {},
    [keyboardAction, onButtonClick],
  );

  /*
   * Render.
   */

  const renderTooltip = () => {
    if (isTicketStatusDropdownOpen) {
      return null;
    }

    if (!offlinePermission.isAllowed) {
      return <PermissionDenied reason={offlinePermission.reason} />;
    }

    return props.renderButtonTooltip?.(isTicketStatusDropdownOpen);
  };

  const isDisabled = !offlinePermission.isAllowed || !areAllTicketingActionsEnabled;

  return (
    <PermissionCheck namespaces={contextNamespaces} permission={PermissionsEnum.CONVERSATION_ARCHIVE}>
      {(archivePermission) => (
        <>
          <TooltipCoordinator render={renderTooltip} hideOnMouseDown>
            <ChromelessButton
              ref={buttonRef}
              onClick={onButtonClick}
              className={props.className}
              hasHover={isTicketStatusDropdownOpen}
              isOpen={isTicketStatusDropdownOpen}
              isDisabled={isDisabled}
            >
              <KeyboardShortcuts handlers={keyboardHandlers} isDisabled={isDisabled}>
                {props.renderButton({
                  isOpen: isTicketStatusDropdownOpen,
                  isDisabled,
                })}
              </KeyboardShortcuts>
            </ChromelessButton>
          </TooltipCoordinator>
          {props.isTicketStatusDropdownOpen && (
            <TicketStatusDropdownContainer
              isArchiveAllowed={archivePermission.isAllowed}
              selectedTicketStatusTagId={indicatedTicketStatus?.tagId}
              onTicketStatusSelect={onTicketStatusSelect}
              anchor={buttonRef}
              onRequestClose={onRequestClose}
              openedByKeyboardAction={openedByKeyboardAction}
            />
          )}
        </>
      )}
    </PermissionCheck>
  );
};
